import { Link, useNavigate, useParams } from 'react-router'
import { useState } from 'react'

import { DataTable } from '@klear/ui/components/data-table'
import type { ColumnDef } from '@tanstack/react-table'

import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import {
  useTraderExternalBankAccounts,
  useTraderExternalBankAccountMutations
} from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useKyc } from '@klear/ui/hooks/use-kyc'
import {
  ROUTE_EXTERNAL_BANK,
  ROUTE_IDENTITY_VERIFICATION,
  PERMISSION_ENTITY_ORGANIZATION,
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_DELETE
} from '@klear/constants'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { DropdownMenuItem } from '@klear/ui/components/dropdown-menu'
import { DataTableRowActions } from '@klear/ui/components/data-table-row-actions'
import type { TraderExternalBankAccount } from '@klear/trader-external-bank-account'
import {
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED
} from '@klear/bank-account/constants'
import { KlearDate } from '@klear/date'
import { buildLegalDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'
import VerifyExternalBankAccount from '@/app/organization/external-bank-account/verify'
import VerificationErrorDialog from '@/app/organization/external-bank-account/verification-error-dialog'
import { useToast } from '@klear/ui/hooks/use-toast'
import { UnverifiedBankAccountBanner } from '@/components/unverified-bank-account-banner'
import { GeneratedLegalAgreementViewer } from '@/app/legal-agreement/generated-legal-agreement-viewer'
import { trpc } from '@klear/ui/lib/trpc'
import PdfPreviewModal from '@klear/ui/components/pdf-preview-modal'
import { CreditCard } from 'lucide-react'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function ExternalBankAccounts() {
  useDocumentTitle('External Bank Accounts')
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const { traderId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { urlPrefixApp } = useTrader(traderId)
  const { kyc } = useKyc()
  const { isLoading, traderExternalBankAccounts } = useTraderExternalBankAccounts()
  const {
    deleteTraderExternalBankAccount,
    initiateVerification,
    isInitiatingVerification,
    resetStripeVerification,
    isResettingStripeVerification
  } = useTraderExternalBankAccountMutations()
  const { toast } = useToast()
  const [verifyingBankAccount, setVerifyingBankAccount] = useState<TraderExternalBankAccount | null>(null)
  const [diagnosticsBankAccount, setDiagnosticsBankAccount] = useState<TraderExternalBankAccount | null>(null)
  const [signingBankAccount, setSigningBankAccount] = useState<TraderExternalBankAccount | null>(null)
  const [viewingSignedPdf, setViewingSignedPdf] = useState<{ url: string; title: string } | null>(null)
  const utils = trpc.useUtils()
  const columns: ColumnDef<TraderExternalBankAccount>[] = [
    {
      header: 'Bank Name',
      accessorKey: 'bankName',
      cell: (info) => info.row.original.bankName
    },
    {
      header: 'Account Number',
      accessorKey: 'accountNumber',
      cell: (info) => info.row.original.accountNumber
    },
    {
      header: 'Routing Number',
      accessorKey: 'routingNumber',
      cell: (info) => info.row.original.routingNumber
    },
    {
      header: 'Account Holder Name',
      accessorKey: 'accountHolderName',
      cell: (info) => info.row.original.accountHolderName
    },
    {
      header: 'Account Holder Type',
      accessorKey: 'accountHolderType',
      cell: (info) => info.row.original.accountHolderType
    },
    {
      header: 'Supported Networks',
      accessorKey: 'supportedPaymentNetworks',
      cell: (info) => (
        <div className="flex flex-wrap gap-2 items-center justify-start">
          {info.row.original.supportedPaymentNetworks.map((network: string) => (
            <Badge key={network} variant="secondary" className="text-xs">
              {network}
            </Badge>
          ))}
        </div>
      )
    },
    {
      header: 'Verification Status',
      accessorKey: 'verificationStatus',
      cell: (info) => {
        const account = info.row.original
        const status = account.verificationStatus
        let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'secondary'
        const isAuthorized = !!account.traderLegalAgreementAcceptanceId

        if (status === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED) {
          variant = isAuthorized ? 'default' : 'outline'
        } else if (status === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING) {
          variant = 'outline'
        } else if (status === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED) {
          variant = 'destructive'
        }

        const estimatedDate = account.estimatedMicrodepositAvailabilityDate

        return (
          <div className="flex flex-col items-start gap-1">
            <Badge variant={variant} className="text-xs">
              {account.verificationStatusLabel}
            </Badge>
            {estimatedDate && status === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING && (
              <span className="text-xs text-muted-foreground">Codes available: {KlearDate.format(estimatedDate)}</span>
            )}
          </div>
        )
      }
    },
    {
      header: 'ACH Agreement',
      accessorKey: 'traderLegalAgreementAcceptanceId',
      cell: (info) => {
        const account = info.row.original
        if (!account.requiresAchAuthorization) {
          return <span className="text-muted-foreground text-xs">—</span>
        }
        const isVerified = account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED
        const isAuthorized = !!account.traderLegalAgreementAcceptanceId
        const canEdit = user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)
        const pdfTitle = `ACH Agreement – ${account.bankName}${account.last4 ? ` ••${account.last4}` : ''}`

        if (!isVerified) {
          return <span className="text-muted-foreground text-xs">—</span>
        }

        const openSignedPdf = () => {
          const key = account.achSignedDocumentKey
          const pdfUrl = key && traderId ? buildLegalDocumentFileUrl(traderId, key) : null
          if (pdfUrl) {
            setViewingSignedPdf({ url: pdfUrl, title: pdfTitle })
          } else {
            toast({
              title: 'Document not found',
              description: 'The signed ACH agreement could not be found.',
              variant: 'destructive'
            })
          }
        }

        if (isAuthorized) {
          return (
            <Button variant="outline" size="sm" onClick={openSignedPdf} className="text-xs">
              View
            </Button>
          )
        }

        if (!canEdit) {
          return (
            <Badge variant="secondary" className="text-xs">
              Not Authorized
            </Badge>
          )
        }

        return (
          <Button
            variant="default"
            size="sm"
            onClick={() => setSigningBankAccount(account)}
            disabled={isLoading}
            className="text-xs"
          >
            Authorize
          </Button>
        )
      }
    },
    {
      id: 'actions',
      cell: (info) => {
        const handleDelete = () => {
          confirm({
            title: 'Delete Bank Account',
            description: 'Are you sure you want to delete this bank account? This action cannot be undone.',
            onConfirm: async () => {
              try {
                if (info.row.original.id == null) return
                await deleteTraderExternalBankAccount({ id: info.row.original.id })
              } catch {}
            }
          })
        }

        const account = info.row.original
        const canVerify =
          account.requiresMicrodepositVerification &&
          (account.canVerify ||
            account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING ||
            (account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED &&
              !!account.stripeSetupIntentId))
        const canInitiateVerification =
          account.requiresMicrodepositVerification &&
          (account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED ||
            account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED) &&
          !account.stripeSetupIntentId

        const canResetStripeVerification =
          user.hasKlearRole &&
          user.hasPermission(PERMISSION_ENTITY_ORGANIZATION, PERMISSION_ACTION_EDIT) &&
          (!!account.stripeSetupIntentId ||
            !!account.stripePaymentMethodId ||
            (account.verificationAttempts ?? 0) > 0 ||
            account.verificationStatus !== EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED)

        const canViewVerificationError =
          user.hasKlearRole && account.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED

        const handleResetStripeVerification = () => {
          confirm({
            title: 'Reset verification',
            description:
              'This clears Stripe setup intent, payment method, and verification attempts, then re-sends microdeposits. The customer can verify once deposits arrive (typically 1–2 business days). Continue?',
            onConfirm: async () => {
              try {
                if (account.id == null) return
                await resetStripeVerification({ id: account.id })
                toast({
                  title: 'Verification reset',
                  description:
                    'Stripe verification was reset and microdeposits were re-sent. The customer can verify once deposits arrive.'
                })
              } catch {}
            }
          })
        }

        const handleInitiateVerification = async () => {
          try {
            if (account.id == null) return
            await initiateVerification({ id: account.id })
            toast({
              title: 'Verification Initiated',
              description:
                'Microdeposits have been sent to your bank account. You will be able to verify once they arrive (typically 1-2 business days).'
            })
          } catch {}
        }

        return (
          <>
            <div className="flex justify-end">
              <DataTableRowActions>
                {canViewVerificationError && (
                  <DropdownMenuItem
                    onClick={() => setDiagnosticsBankAccount(account)}
                    disabled={isLoading}
                    className="hover:text-muted-foreground"
                  >
                    View verification error
                  </DropdownMenuItem>
                )}
                {canResetStripeVerification && (
                  <DropdownMenuItem
                    onClick={handleResetStripeVerification}
                    disabled={isLoading || isResettingStripeVerification}
                    className="hover:text-muted-foreground"
                  >
                    Reset verification
                  </DropdownMenuItem>
                )}
                {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && canInitiateVerification && (
                  <DropdownMenuItem
                    onClick={handleInitiateVerification}
                    disabled={isLoading || isInitiatingVerification}
                    className="hover:text-muted-foreground"
                  >
                    Initiate Verification
                  </DropdownMenuItem>
                )}
                {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && canVerify && (
                  <DropdownMenuItem
                    onClick={() => setVerifyingBankAccount(account)}
                    disabled={isLoading}
                    className="hover:text-muted-foreground"
                  >
                    Verify
                  </DropdownMenuItem>
                )}
                {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
                  <DropdownMenuItem
                    onClick={() => navigate(`${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}/${account.id}/edit`)}
                    disabled={isLoading}
                    className="hover:text-muted-foreground"
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_DELETE) && (
                  <DropdownMenuItem onClick={handleDelete} disabled={isLoading} className="hover:text-muted-foreground">
                    Delete
                  </DropdownMenuItem>
                )}
              </DataTableRowActions>
            </div>
            {ConfirmDialog}
          </>
        )
      }
    }
  ]

  return (
    <div className="container mx-auto px-2 py-6">
      <UnverifiedBankAccountBanner className="mb-6" />
      <h1 className="text-2xl font-bold mb-6">External Bank Accounts</h1>
      <div className="text-muted-foreground">Connect your external bank accounts to enable payments and transfers</div>

      {kyc?.id ? (
        <>
          <div className="flex justify-end items-center mb-6 gap-2">
            <Button asChild>
              <Link to={`${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}/new`}>Add bank account</Link>
            </Button>
          </div>

          {!isLoading && traderExternalBankAccounts.length === 0 ? (
            <div className="rounded-lg border border-dashed bg-card p-12 text-center">
              <CreditCard className="mx-auto h-10 w-10 text-muted-foreground" />
              <h3 className="mt-4 text-sm font-semibold">No bank accounts yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Connect an external bank account to send and receive payments.
              </p>
              <Button asChild className="mt-4">
                <Link to={`${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}/new`}>Add bank account</Link>
              </Button>
            </div>
          ) : (
            <DataTable data={traderExternalBankAccounts} columns={columns} loading={isLoading} />
          )}
        </>
      ) : (
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-950 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                You need to start the KYC process before adding external bank accounts.{' '}
                <Link
                  to={`${urlPrefixApp}/${ROUTE_IDENTITY_VERIFICATION}`}
                  className="font-medium underline hover:text-yellow-600"
                >
                  Start KYC process
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {verifyingBankAccount && (
        <VerifyExternalBankAccount
          externalBankAccount={verifyingBankAccount}
          open={!!verifyingBankAccount}
          onOpenChange={(open) => !open && setVerifyingBankAccount(null)}
        />
      )}

      {diagnosticsBankAccount?.id != null && (
        <VerificationErrorDialog
          accountId={diagnosticsBankAccount.id}
          bankName={diagnosticsBankAccount.bankName}
          open={!!diagnosticsBankAccount}
          onOpenChange={(open) => !open && setDiagnosticsBankAccount(null)}
        />
      )}

      <GeneratedLegalAgreementViewer
        traderId={parseInt(traderId || '0')}
        open={!!signingBankAccount}
        onClose={() => setSigningBankAccount(null)}
        onSuccess={() => {
          setSigningBankAccount(null)
          utils.traderExternalBankAccount.list.invalidate()
        }}
        externalAccountIds={signingBankAccount && signingBankAccount.id != null ? [signingBankAccount.id] : []}
        klearAccountIds={[]}
        accountId={signingBankAccount?.id}
        accountType="external"
      />

      {viewingSignedPdf && (
        <PdfPreviewModal
          title={viewingSignedPdf.title}
          pdfUrl={viewingSignedPdf.url}
          downloadUrl={withAttachmentDisposition(viewingSignedPdf.url)}
          open={!!viewingSignedPdf}
          onOpenChange={(open) => !open && setViewingSignedPdf(null)}
          downloadable
        />
      )}
    </div>
  )
}
